import { useEffect, useState } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Save,
} from "lucide-react";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { MultiSelect } from "ui/walkinMulti-select";
import { Label } from "ui/label";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Card, CardContent } from "ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import Pagination from "ui/pagination";
import { getVenuesOptions } from "services/venueService";
import {
  getAvailableProperties,
  AvailablePropertyApi,
  getRoomTypesByProperty,
  RoomTypeByPropertyApi,
  getAvailableRooms,
  AvailableRoomApi,
  createWalkInRoomAllocation,
  getWalkInRoomAllocations,
  updateWalkInRoomAllocation,
  deleteWalkInRoomAllocation,
} from "services/tourWalkinService";
import { FullScreenLoading } from "components/ui/loader";
import { toast } from "sonner";
import axios from "axios";

interface Room {
  id: string;
  venueId: string;
  venueName: string;
  propertyId: string;
  propertyName: string;
  roomTypeId: string;
  roomType: string;
  roomNameId: string[];
  roomNames: string;
  capacity: number;
}

interface DropdownOption {
  id: string;
  name: string;
}

interface PropertyRoomTypeOption {
  id: string;
  publicId: string;
  name: string;
}

interface VenueSummaryItem {
  id: string;
  venueId: number | null;
  venuePublicId?: string;
  venueName: string;
  propertyTypeId?: string;
  fromDate: string;
  toDate: string;
}

interface FaizAccommodationProps {
  onRoomsChange: (rooms: Room[]) => void;
  venueSummary: VenueSummaryItem[];
  venueApiOptions: {
    venueId: number;
    publicId: string;
    venueName: string;
  }[];
  tourPublicId: string;
}

export function FaizAccommodation({
  onRoomsChange,
  venueSummary,
  venueApiOptions,
  tourPublicId,
}: FaizAccommodationProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  useEffect(() => {
    onRoomsChange(rooms);
  }, [rooms, onRoomsChange]);
  const [venues, setVenues] = useState<DropdownOption[]>([]);
  const [venueLoading, setVenueLoading] = useState(false);
  const [properties, setProperties] = useState<DropdownOption[]>([]);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [roomTypes, setRoomTypes] = useState<PropertyRoomTypeOption[]>([]);
  const [roomNames, setRoomNames] = useState<DropdownOption[]>([]);
  const [roomTypeLoading, setRoomTypeLoading] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [deletingItem, setDeletingItem] = useState<Room | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const loadRoomAllocations = async () => {
      if (!tourPublicId) {
        setRooms([]);
        return;
      }

      try {
        setLoading(true);

        const response = await getWalkInRoomAllocations(tourPublicId);

        if (!response.success) {
          console.error(
            "Failed to load room allocations:",
            response.message,
            response.errors,
          );
          setRooms([]);
          return;
        }

        const apiRooms: Room[] = response.data.map((allocation) => ({
          id: allocation.roomAllocationPublicId,
          venueId:
            venueApiOptions
              .find((venue) => venue.venueName === allocation.venueName)
              ?.venueId.toString() || "",
          venueName: allocation.venueName,
          propertyId: "",
          propertyName: allocation.propertyName,
          roomTypeId: "",
          roomType: allocation.roomType,
          roomNameId: [],
          roomNames: allocation.roomNames.join(", "),
          capacity: allocation.capacity,
        }));

        setRooms(apiRooms);
        setPageNo(1);
      } catch (error) {
        console.error("Failed to load room allocations:", error);
        setRooms([]);
      } finally {
        setLoading(false);
      }
    };

    loadRoomAllocations();
  }, [tourPublicId, venueApiOptions]);

  useEffect(() => {
    const loadVenues = async () => {
      try {
        setVenueLoading(true);
        const response = await getVenuesOptions();
        const venueOptions: DropdownOption[] = Array.isArray(response)
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            response.map((venue: any) => ({
              id: String(venue.venueId),
              name: venue.venueName,
            }))
          : [];
        setVenues(venueOptions);
      } catch (error) {
        console.error("Failed to load venues:", error);
        setVenues([]);
      } finally {
        setVenueLoading(false);
      }
    };
    loadVenues();
  }, []);

  const formatDateForPropertyApi = (dateValue: string) => {
    if (!dateValue) {
      return "";
    }
    const [datePart] = dateValue.split("T");
    // Convert YYYY-MM-DD -> DD-MM-YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [year, month, day] = datePart.split("-");
      return `${day}-${month}-${year}`;
    }
    // Already DD-MM-YYYY
    if (/^\d{2}-\d{2}-\d{4}$/.test(datePart)) {
      return datePart;
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      console.error("Invalid venue summary date:", dateValue);
      return "";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${day}-${month}-${year}`;
  };

  const loadAvailableProperties = async (venueId: string) => {
    if (!venueId) {
      setProperties([]);
      return;
    }

    const selectedVenue = venueApiOptions.find(
      (venue) => String(venue.venueId) === String(venueId),
    );

    if (!selectedVenue) {
      console.warn("Selected venue not found in Venue API:", venueId);
      setProperties([]);
      return;
    }

    const selectedVenueSummary = venueSummary.find(
      (venue) => String(venue.venueId) === String(venueId),
    );

    if (!selectedVenueSummary) {
      console.warn("No venue schedule found for selected venue:", venueId);
      setProperties([]);
      return;
    }

    const venuePublicId = selectedVenue.publicId;

    if (
      !venuePublicId ||
      !selectedVenueSummary.fromDate ||
      !selectedVenueSummary.toDate
    ) {
      console.warn("Missing property API parameters:", {
        venuePublicId,
        fromDate: selectedVenueSummary.fromDate,
        toDate: selectedVenueSummary.toDate,
      });

      setProperties([]);
      return;
    }

    try {
      setPropertyLoading(true);

      const availableProperties = await getAvailableProperties(
        venuePublicId,
        formatDateForPropertyApi(selectedVenueSummary.fromDate),
        formatDateForPropertyApi(selectedVenueSummary.toDate),
        selectedVenueSummary.propertyTypeId || "",
      );

      const propertyOptions: DropdownOption[] = availableProperties.map(
        (property: AvailablePropertyApi) => ({
          id: property.propertyPublicId,
          name: property.propertyName,
        }),
      );

      setProperties(propertyOptions);
    } catch (error) {
      console.error("Failed to load available properties:", error);
      setProperties([]);
    } finally {
      setPropertyLoading(false);
    }
  };

  const loadPropertyRoomTypes = async (propertyPublicId: string) => {
    if (!propertyPublicId) {
      setRoomTypes([]);
      setRoomNames([]);
      return;
    }

    try {
      setRoomTypeLoading(true);

      const roomTypeResponse = await getRoomTypesByProperty(propertyPublicId);

      const propertyRoomTypes: PropertyRoomTypeOption[] = roomTypeResponse
        .filter((roomType: RoomTypeByPropertyApi) => roomType.isActive)
        .map((roomType: RoomTypeByPropertyApi) => ({
          id: String(roomType.id),
          publicId: roomType.publicId,
          name: roomType.roomName,
        }));

      setRoomTypes(propertyRoomTypes);
      setRoomNames([]);
    } catch (error) {
      console.error("Failed to load room types for property:", error);
      setRoomTypes([]);
      setRoomNames([]);
    } finally {
      setRoomTypeLoading(false);
    }
  };

  const loadAvailableRooms = async (roomTypeId: string) => {
    if (!roomTypeId || !formData.propertyId || !formData.venueId) {
      setRoomNames([]);
      return;
    }
    const selectedVenueSummary = venueSummary.find(
      (venue) => String(venue.venueId) === String(formData.venueId),
    );

    if (!selectedVenueSummary) {
      console.warn(
        "No venue summary found for selected venue:",
        formData.venueId,
      );
      setRoomNames([]);
      return;
    }
    const selectedVenue = venueApiOptions.find(
      (venue) => String(venue.venueId) === String(formData.venueId),
    );

    if (!selectedVenue?.publicId) {
      console.warn(
        "No venuePublicId found for selected venue:",
        formData.venueId,
        selectedVenue,
      );
      setRoomNames([]);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const selectedProperty = properties.find(
      (property) => property.id === formData.propertyId,
    );

    const selectedRoomType = roomTypes.find(
      (roomType) => roomType.id === roomTypeId,
    );

    if (!selectedRoomType?.publicId) {
      console.warn(
        "No room type publicId found:",
        roomTypeId,
        selectedRoomType,
      );
      setRoomNames([]);
      return;
    }

    if (!selectedVenueSummary.fromDate || !selectedVenueSummary.toDate) {
      console.warn(
        "Venue summary is missing fromDate/toDate:",
        selectedVenueSummary,
      );
      setRoomNames([]);
      return;
    }

    const startDate = formatDateForPropertyApi(selectedVenueSummary.fromDate);

    const endDate = formatDateForPropertyApi(selectedVenueSummary.toDate);

    try {
      console.log("Calling available rooms API with:", {
        propertyPublicId: formData.propertyId,
        venuePublicId: selectedVenue.publicId,
        startDate,
        endDate,
        roomTypePublicId: selectedRoomType.publicId,
      });

      const availableRooms = await getAvailableRooms(
        formData.propertyId,
        selectedVenue.publicId,
        startDate,
        endDate,
        selectedRoomType.publicId,
      );

      console.log("Available rooms API response:", availableRooms);

      const roomOptions: DropdownOption[] = availableRooms
        .filter((room: AvailableRoomApi) => room.isBooked === false)
        .map((room: AvailableRoomApi) => ({
          id: `${room.roomPublicId}-${room.roomNumber}`,
          name: room.roomNumber,
        }));

      console.log("Room dropdown options:", roomOptions);

      setRoomNames(roomOptions);
    } catch (error) {
      console.error("Failed to load available rooms:", error);
      setRoomNames([]);
    }
  };

  const [formData, setFormData] = useState({
    venueId: "",
    propertyId: "",
    roomTypeId: "",
    roomNameId: [] as string[],
    capacity: 1,
  });

  const [errors, setErrors] = useState({
    venueId: "",
    propertyId: "",
    roomTypeId: "",
    roomNameId: "",
    capacity: "",
  });

  const resetForm = () => {
    setFormData({
      venueId: "",
      propertyId: "",
      roomTypeId: "",
      roomNameId: [],
      capacity: 1,
    });

    setErrors({
      venueId: "",
      propertyId: "",
      roomTypeId: "",
      roomNameId: "",
      capacity: "",
    });

    setEditingRoom(null);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const filteredRooms = [...rooms]
    .filter((room) => {
      const searchValue = [
        room.venueName,
        room.propertyName,
        room.roomType,
        room.roomNames,
        room.capacity.toString(),
      ]
        .join(" ")
        .toLowerCase();
      return searchValue.includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      if (!sortColumn) {
        return 0;
      }
      const aValue = a[sortColumn as keyof Room];
      const bValue = b[sortColumn as keyof Room];
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });

  const totalCount = filteredRooms.length;
  const startIndex = (pageNo - 1) * pageSize;
  const paginatedRooms = filteredRooms.slice(startIndex, startIndex + pageSize);

  const handleSaveRoom = async () => {
    const newErrors = {
      venueId: "",
      propertyId: "",
      roomTypeId: "",
      roomNameId: "",
      capacity: "",
    };

    if (!formData.venueId) {
      newErrors.venueId = "Venue Name is required";
    }

    if (!formData.propertyId) {
      newErrors.propertyId = "Property Name is required";
    }

    if (!formData.roomTypeId) {
      newErrors.roomTypeId = "Room Type is required";
    }

    if (formData.roomNameId.length === 0) {
      newErrors.roomNameId = "Room Name is required";
    }

    if (!formData.capacity || formData.capacity < 1) {
      newErrors.capacity = "Capacity must be at least 1";
    }

    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      return;
    }

    // Start full-screen loading only after validation succeeds
    setLoading(true);

    try {
      const selectedVenue = venues.find((item) => item.id === formData.venueId);

      const selectedProperty = properties.find(
        (item) => item.id === formData.propertyId,
      );

      const selectedRoomType = roomTypes.find(
        (item) => item.id === formData.roomTypeId,
      );

      const selectedRoomNames = roomNames.filter((item) =>
        formData.roomNameId.includes(item.id),
      );

      const venueId = Number(formData.venueId);
      const propertyPublicId = formData.propertyId;
      const propertyRoomTypeId = Number(formData.roomTypeId);

      const roomNamesPayload = [
        selectedRoomNames.map((room) => room.name).join(","),
      ];

      // =========================================================
      // EDIT / UPDATE
      // =========================================================
      if (editingRoom) {
        if (!tourPublicId) {
          console.error("Tour public ID is missing.");
          return;
        }

        const response = await updateWalkInRoomAllocation(editingRoom.id, {
          tourPublicId,
          roomName: selectedRoomNames.map((room) => room.name).join(","),
          capacity: formData.capacity,
        });

        if (!response.success) {
          console.error(
            "Room allocation update failed:",
            response.message,
            response.errors,
          );
          toast.error(response.message || "Failed to update room allocation");
          return;
        }

        console.log("Room allocation updated:", response.data);

        toast.success(
          response.message || "Walk-in room allocation updated successfully.",
        );

        // Refresh the table from API after PUT
        const allocationsResponse =
          await getWalkInRoomAllocations(tourPublicId);

        if (allocationsResponse.success) {
          const apiRooms: Room[] = allocationsResponse.data.map(
            (allocation) => ({
              id: allocation.roomAllocationPublicId,
              venueId:
                venueApiOptions
                  .find((venue) => venue.venueName === allocation.venueName)
                  ?.venueId.toString() || "",
              venueName: allocation.venueName,
              propertyId: "",
              propertyName: allocation.propertyName,
              roomTypeId: "",
              roomType: allocation.roomType,
              roomNameId: [],
              roomNames: allocation.roomNames.join(", "),
              capacity: allocation.capacity,
            }),
          );

          setRooms(apiRooms);
          setPageNo(1);
        }

        setIsDialogOpen(false);
        resetForm();

        return;
      }

      // =========================================================
      // CREATE / POST
      // =========================================================

      if (!tourPublicId) {
        console.error("Tour public ID is missing.");
        return;
      }

      if (!selectedVenue) {
        console.error("Selected venue not found.");
        return;
      }

      if (!selectedProperty) {
        console.error("Selected property not found.");
        return;
      }

      if (!selectedRoomType) {
        console.error("Selected room type not found.");
        return;
      }

      const payload = {
        tourPublicId,
        venueId,
        propertyPublicId,
        propertyRoomTypeId,
        roomNames: roomNamesPayload,
        capacity: formData.capacity,
      };

      const response = await createWalkInRoomAllocation(payload);

      if (!response.success) {
        console.error(
          "Room allocation API failed:",
          response.message,
          response.errors,
        );
        toast.error(response.message || "Failed to create room allocation");
        return;
      }

      console.log("Room allocation created:", response.data);

      toast.success(
        response.message || "Walk-in room allocation created successfully.",
      );

      // Refresh the table from API after POST
      const allocationsResponse = await getWalkInRoomAllocations(tourPublicId);

      if (allocationsResponse.success) {
        const apiRooms: Room[] = allocationsResponse.data.map((allocation) => ({
          id: allocation.roomAllocationPublicId,
          venueId:
            venueApiOptions
              .find((venue) => venue.venueName === allocation.venueName)
              ?.venueId.toString() || "",
          venueName: allocation.venueName,
          propertyId: "",
          propertyName: allocation.propertyName,
          roomTypeId: "",
          roomType: allocation.roomType,
          roomNameId: [],
          roomNames: allocation.roomNames.join(", "),
          capacity: allocation.capacity,
        }));

        setRooms(apiRooms);
        setPageNo(1);
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Failed to save room allocation:", error);

      toast.error(
        axios.isAxiosError(error)
          ? error.response?.data?.message || "Failed to save room allocation."
          : error instanceof Error
            ? error.message
            : "Failed to save room allocation.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (room: Room) => {
    setLoading(true);
    setEditingRoom(room);

    setErrors({
      venueId: "",
      propertyId: "",
      roomTypeId: "",
      roomNameId: "",
      capacity: "",
    });

    setFormData({
      venueId: room.venueId,
      propertyId: "",
      roomTypeId: "",
      roomNameId: [],
      capacity: room.capacity,
    });

    setIsDialogOpen(true);

    try {
      // 1. Load properties for the selected venue
      const availableProperties = await getAvailableProperties(
        venueApiOptions.find(
          (venue) => String(venue.venueId) === String(room.venueId),
        )?.publicId || "",
        formatDateForPropertyApi(
          venueSummary.find(
            (venue) => String(venue.venueId) === String(room.venueId),
          )?.fromDate || "",
        ),
        formatDateForPropertyApi(
          venueSummary.find(
            (venue) => String(venue.venueId) === String(room.venueId),
          )?.toDate || "",
        ),
        venueSummary.find(
          (venue) => String(venue.venueId) === String(room.venueId),
        )?.propertyTypeId || "",
      );

      const propertyOptions: DropdownOption[] = availableProperties.map(
        (property: AvailablePropertyApi) => ({
          id: property.propertyPublicId,
          name: property.propertyName,
        }),
      );

      setProperties(propertyOptions);

      // 2. Find the property from the API response using propertyName
      const selectedProperty = propertyOptions.find(
        (property) => property.name === room.propertyName,
      );

      if (!selectedProperty) {
        console.error("Property not found for room:", room.propertyName);
        return;
      }

      // 3. Load room types for that property
      setRoomTypeLoading(true);

      const roomTypeResponse = await getRoomTypesByProperty(
        selectedProperty.id,
      );

      const propertyRoomTypes: PropertyRoomTypeOption[] = roomTypeResponse
        .filter((roomType: RoomTypeByPropertyApi) => roomType.isActive)
        .map((roomType: RoomTypeByPropertyApi) => ({
          id: String(roomType.id),
          publicId: roomType.publicId,
          name: roomType.roomName,
        }));

      setRoomTypes(propertyRoomTypes);

      // 4. Find the room type using roomType name
      const selectedRoomType = propertyRoomTypes.find(
        (roomType) => roomType.name === room.roomType,
      );

      if (!selectedRoomType) {
        console.error("Room type not found:", room.roomType);
        return;
      }

      // 5. Load available rooms
      const selectedVenue = venueApiOptions.find(
        (venue) => String(venue.venueId) === String(room.venueId),
      );

      const selectedVenueSummary = venueSummary.find(
        (venue) => String(venue.venueId) === String(room.venueId),
      );

      if (
        !selectedVenue?.publicId ||
        !selectedVenueSummary?.fromDate ||
        !selectedVenueSummary?.toDate
      ) {
        console.error("Venue information is missing for room edit.");
        return;
      }

      const availableRooms = await getAvailableRooms(
        selectedProperty.id,
        selectedVenue.publicId,
        formatDateForPropertyApi(selectedVenueSummary.fromDate),
        formatDateForPropertyApi(selectedVenueSummary.toDate),
        selectedRoomType.publicId,
      );

      const propertyRoomNames: DropdownOption[] = availableRooms
        .filter(
          (availableRoom: AvailableRoomApi) => availableRoom.isBooked === false,
        )
        .map((availableRoom: AvailableRoomApi) => ({
          id: `${availableRoom.roomPublicId}-${availableRoom.roomNumber}`,
          name: availableRoom.roomNumber,
        }));

      // Make sure the currently allocated room is available
      // in the edit dropdown even if the API marks it as booked.
      const existingRoomNames = room.roomNames
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean);

      existingRoomNames.forEach((existingRoomName) => {
        if (
          !propertyRoomNames.some(
            (roomName) => roomName.name === existingRoomName,
          )
        ) {
          propertyRoomNames.push({
            id: existingRoomName,
            name: existingRoomName,
          });
        }
      });

      setRoomNames(propertyRoomNames);

      // 6. Select existing values
      const selectedRoomNames = propertyRoomNames.filter((roomName) =>
        existingRoomNames.includes(roomName.name),
      );

      setFormData({
        venueId: room.venueId,
        propertyId: selectedProperty.id,
        roomTypeId: selectedRoomType.id,
        roomNameId: selectedRoomNames.map((roomName) => roomName.id),
        capacity: room.capacity,
      });
    } catch (error) {
      console.error("Failed to load property room details:", error);

      setRoomTypes([]);
      setRoomNames([]);
    } finally {
      setRoomTypeLoading(false);
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingItem) {
      return;
    }

    if (!tourPublicId) {
      toast.error("Tour public ID is missing.");
      return;
    }

    setLoading(true);

    try {
      const response = await deleteWalkInRoomAllocation(
        deletingItem.id,
        tourPublicId,
      );

      if (!response.success) {
        console.error(
          "Room allocation delete failed:",
          response.message,
          response.errors,
        );

        toast.error(response.message || "Failed to delete room allocation.");

        return;
      }

      toast.success(
        response.message || "Walk-in room allocation deleted successfully.",
      );

      // Refresh the table from API after DELETE
      const allocationsResponse = await getWalkInRoomAllocations(tourPublicId);

      if (allocationsResponse.success) {
        const apiRooms: Room[] = allocationsResponse.data.map((allocation) => ({
          id: allocation.roomAllocationPublicId,
          venueId:
            venueApiOptions
              .find((venue) => venue.venueName === allocation.venueName)
              ?.venueId.toString() || "",
          venueName: allocation.venueName,
          propertyId: "",
          propertyName: allocation.propertyName,
          roomTypeId: "",
          roomType: allocation.roomType,
          roomNameId: [],
          roomNames: allocation.roomNames.join(", "),
          capacity: allocation.capacity,
        }));

        setRooms(apiRooms);
        setPageNo(1);
      }

      setIsDeleteDialogOpen(false);
      setDeletingItem(null);
    } catch (error) {
      console.error("Failed to delete room allocation:", error);

      toast.error("Failed to delete room allocation.");
    } finally {
      setLoading(false);
    }
  };

  const SortHeader = ({ column, label }: { column: string; label: string }) => (
    <TableHead
      style={{ color: "#5B9BD5" }}
      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortColumn === column ? (
          sortDirection === "asc" ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
          <div className="relative flex-1 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search rooms..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="h-10 pl-9 pr-9 shadow-sm"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setPageNo(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              onClick={() => {
                resetForm();
                setIsDialogOpen(true);
              }}
              style={{ background: "#5B9BD5" }}
              className="text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Room
            </Button>
          </div>
        </div>

        <Card
          className="bg-white border-[#5B9BD5]/30 hidden lg:block"
          style={{ marginTop: "10px" }}
        >
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  <SortHeader column="venueName" label="Venue Name" />
                  <SortHeader column="propertyName" label="Property Name" />
                  <SortHeader column="roomType" label="Room Type" />
                  <SortHeader column="roomNames" label="Room Names" />
                  <SortHeader column="capacity" label="Capacity" />
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs text-right"
                  >
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedRooms.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 px-4 text-gray-500 text-sm"
                    >
                      {searchTerm
                        ? "No rooms found matching your search"
                        : "No rooms found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRooms.map((room) => (
                    <TableRow key={room.id} className="hover:bg-sky-50/30">
                      <TableCell className="px-4 py-2">
                        <span className="text-gray-900 text-sm font-medium">
                          {room.venueName}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <span className="text-gray-700 text-sm">
                          {room.propertyName}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <span className="text-gray-700 text-sm">
                          {room.roomType}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <span className="text-gray-700 text-sm">
                          {room.roomNames}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <span className="text-gray-900 text-sm font-medium">
                          {room.capacity}
                        </span>
                      </TableCell>
                      <TableCell
                        className="px-4 py-2 text-right"
                        style={{ gap: "0px" }}
                      >
                        <div className="flex justify-end">
                          <Button
                            onClick={() => handleEdit(room)}
                            size="sm"
                            variant="ghost"
                            className="text-[#5B9BD5] hover:text-[#4A8BC2] hover:bg-sky-50 h-8 w-8"
                            style={{
                              padding: "0px",
                              gap: "0px",
                            }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => {
                              setDeletingItem(room);
                              setIsDeleteDialogOpen(true);
                            }}
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                            style={{
                              padding: "0px",
                              gap: "0px",
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="rooms"
          />
        </Card>

        <div className="lg:hidden space-y-4">
          {paginatedRooms.length === 0 ? (
            <Card className="border-[#5B9BD5]/30">
              <CardContent className="p-8 text-center">
                <p className="text-gray-500 text-sm">
                  {searchTerm
                    ? "No rooms found matching your search"
                    : "No rooms found"}
                </p>
              </CardContent>
            </Card>
          ) : (
            paginatedRooms.map((room) => (
              <Card key={room.id} className="border-[#5B9BD5]/30 shadow-sm">
                <CardContent style={{ padding: "14px" }}>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500">Venue Name</p>
                      <p className="text-sm font-medium text-gray-900">
                        {room.venueName}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Property Name</p>
                      <p className="text-sm text-gray-900">
                        {room.propertyName}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500">Room Type</p>
                        <p className="text-sm text-gray-900">{room.roomType}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Room Names</p>
                        <p className="text-sm text-gray-900">
                          {room.roomNames}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Capacity</p>
                      <p className="text-sm font-medium text-gray-900">
                        {room.capacity}
                      </p>
                    </div>
                    <div className="flex justify-end border-t border-gray-100 pt-2">
                      <Button
                        onClick={() => handleEdit(room)}
                        size="sm"
                        variant="ghost"
                        className="text-[#5B9BD5] hover:text-[#4A8BC2] hover:bg-sky-50 h-8 w-8"
                        style={{
                          padding: "0px",
                          gap: "0px",
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => {
                          setDeletingItem(room);
                          setIsDeleteDialogOpen(true);
                        }}
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                        style={{
                          padding: "0px",
                          gap: "0px",
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="rooms"
          />
        </div>
        <Dialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            setIsDialogOpen(open);

            if (!open) {
              resetForm();
            }
          }}
        >
          <DialogContent className="bg-white sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                {editingRoom ? "Edit Room" : "Add Room"}
              </DialogTitle>
              <DialogDescription>
                {editingRoom ? "Update room details" : "Create a new room"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Venue Name */}
              <div className="space-y-2">
                <SelectFieldset label="Venue Name" required>
                  <Select
                    value={formData.venueId}
                    onValueChange={async (value) => {
                      setFormData({
                        ...formData,
                        venueId: value,
                        propertyId: "",
                        roomTypeId: "",
                        roomNameId: [],
                      });

                      setErrors({
                        ...errors,
                        venueId: "",
                        propertyId: "",
                        roomTypeId: "",
                        roomNameId: "",
                      });

                      setProperties([]);
                      setRoomTypes([]);
                      setRoomNames([]);

                      await loadAvailableProperties(value);
                    }}
                  >
                    <SelectTriggerUI
                      className={`h-10 text-sm ${
                        errors.venueId ? "border-red-500" : "border-gray-200"
                      }`}
                    >
                      <SelectValue placeholder="Select venue" />
                    </SelectTriggerUI>
                    <SelectContent>
                      {venueLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading venues...
                        </SelectItem>
                      ) : venues.length === 0 ? (
                        <SelectItem value="no-venues" disabled>
                          No venues available
                        </SelectItem>
                      ) : (
                        venues.map((venue) => (
                          <SelectItem key={venue.id} value={venue.id}>
                            {venue.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {errors.venueId && (
                  <p className="text-red-500 text-xs">{errors.venueId}</p>
                )}
              </div>
              {/* Property Name */}
              <div className="space-y-2">
                <SelectFieldset label="Property Name" required>
                  <Select
                    value={formData.propertyId}
                    onValueChange={async (value) => {
                      setFormData({
                        ...formData,
                        propertyId: value,
                        roomTypeId: "",
                        roomNameId: [],
                      });

                      setErrors({
                        ...errors,
                        propertyId: "",
                        roomTypeId: "",
                        roomNameId: "",
                      });

                      setRoomTypes([]);
                      setRoomNames([]);

                      await loadPropertyRoomTypes(value);
                    }}
                  >
                    <SelectTriggerUI
                      className={`h-10 text-sm ${
                        errors.propertyId ? "border-red-500" : "border-gray-200"
                      }`}
                    >
                      <SelectValue placeholder="Select property" />
                    </SelectTriggerUI>
                    <SelectContent>
                      {propertyLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading properties...
                        </SelectItem>
                      ) : properties.length === 0 ? (
                        <SelectItem value="no-properties" disabled>
                          No properties available
                        </SelectItem>
                      ) : (
                        properties.map((property) => (
                          <SelectItem key={property.id} value={property.id}>
                            {property.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {errors.propertyId && (
                  <p className="text-red-500 text-xs">{errors.propertyId}</p>
                )}
              </div>
              {/* Room Type */}
              <div className="space-y-2">
                <SelectFieldset label="Room Type" required>
                  <Select
                    value={formData.roomTypeId}
                    disabled={!formData.propertyId || roomTypeLoading}
                    onValueChange={async (value) => {
                      setFormData({
                        ...formData,
                        roomTypeId: value,
                        roomNameId: [],
                      });

                      setErrors({
                        ...errors,
                        roomTypeId: "",
                        roomNameId: "",
                      });

                      setRoomNames([]);

                      await loadAvailableRooms(value);
                    }}
                  >
                    <SelectTriggerUI
                      className={`h-10 text-sm ${
                        errors.roomTypeId ? "border-red-500" : "border-gray-200"
                      }`}
                    >
                      <SelectValue
                        placeholder={
                          !formData.propertyId
                            ? "Select property first"
                            : roomTypeLoading
                              ? "Loading room types..."
                              : "Select room type"
                        }
                      />
                    </SelectTriggerUI>

                    <SelectContent>
                      {roomTypeLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading room types...
                        </SelectItem>
                      ) : roomTypes.length === 0 ? (
                        <SelectItem value="no-room-types" disabled>
                          No room types available
                        </SelectItem>
                      ) : (
                        roomTypes.map((roomType) => (
                          <SelectItem key={roomType.id} value={roomType.id}>
                            {roomType.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </SelectFieldset>

                {errors.roomTypeId && (
                  <p className="text-red-500 text-xs">{errors.roomTypeId}</p>
                )}
              </div>

              {/* Room Names */}
              {/* Room Names */}
              <div className="space-y-2">
                <Label>
                  Room Names <span className="text-red-500">*</span>
                </Label>

                <MultiSelect
                  options={roomNames.map((room) => ({
                    value: room.id,
                    label: room.name,
                  }))}
                  selected={formData.roomNameId}
                  onChange={(selected) => {
                    setFormData((prev) => ({
                      ...prev,
                      roomNameId: selected,
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      roomNameId: "",
                    }));
                  }}
                  placeholder={
                    !formData.roomTypeId
                      ? "Select room type first"
                      : roomNames.length === 0
                        ? "No room names available"
                        : "Select room names..."
                  }
                  disabled={!formData.roomTypeId || roomNames.length === 0}
                />

                {errors.roomNameId && (
                  <p className="text-red-500 text-xs">{errors.roomNameId}</p>
                )}
              </div>

              {/* Capacity */}
              <div className="space-y-2">
                <InputFieldset
                  label="Capacity"
                  required
                  id="capacity"
                  type="number"
                  min={1}
                  step={1}
                  value={formData.capacity}
                  onChange={(e) => {
                    const value = Number(e.target.value);

                    setFormData({
                      ...formData,
                      capacity: value,
                    });

                    setErrors({
                      ...errors,
                      capacity: "",
                    });
                  }}
                  className={`bg-white ${
                    errors.capacity
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200"
                  }`}
                />

                {errors.capacity && (
                  <p className="text-red-500 text-xs">{errors.capacity}</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>

              <Button
                type="button"
                onClick={handleSaveRoom}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingRoom ? "Update Room" : "Save Room"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px] bg-white">
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Room</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  "{deletingItem?.roomNames}"
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                }}
              >
                No, Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
